import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';

export default function HealthStatusPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';

  const q = useQuery({
    queryKey: ['health'],
    queryFn: async () => (await api.get('/health')).data,
    refetchInterval: 30000,
  });

  return (
    <div data-testid="health-page">
      <PageHeader title={t('health.title')} subtitle={t('health.subtitle')} />
      {q.isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-card border-border" data-testid="health-mongo-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                <span>MongoDB</span>
                <StatusIcon ok={q.data?.mongo?.ok} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {q.data?.mongo?.error && <div className="text-xs text-[hsl(var(--status-danger))]">{q.data.mongo.error}</div>}
              <div className="text-xs text-muted-foreground">Persisted data layer</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border" data-testid="health-tiktok-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                <span>TikTok Shop API</span>
                <StatusIcon ok={q.data?.tiktok?.ok} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground">mode: <span className="font-mono">{q.data?.tiktok?.mode}</span></div>
              {q.data?.tiktok?.shop && (
                <div className="text-xs text-muted-foreground">shop: <span className="font-mono">{q.data.tiktok.shop}</span></div>
              )}
              {q.data?.tiktok?.reason && (
                <div className="text-xs text-[hsl(var(--status-warning))]">{q.data.tiktok.reason}</div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-card border-border" data-testid="health-qbo-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                <span>QuickBooks Online</span>
                <StatusIcon ok={q.data?.qbo?.ok} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground">mode: <span className="font-mono">{q.data?.qbo?.mode}</span></div>
              {q.data?.qbo?.records != null && (
                <div className="text-xs text-muted-foreground">records: {q.data.qbo.records}</div>
              )}
              {q.data?.qbo?.reason && (
                <div className="text-xs text-[hsl(var(--status-warning))]">{q.data.qbo.reason}</div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-card border-border" data-testid="health-meta-card">
            <CardHeader><CardTitle className="text-base">Meta</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <div className="text-xs">{t('health.mode')}: <span className="font-mono uppercase">{q.data?.adapter_mode}</span></div>
              <div className="text-xs">{t('health.env')}: <span className="font-mono uppercase">{q.data?.env_name}</span></div>
              {q.data?.last_settlement && (
                <div className="text-xs mt-2">{t('health.last_settlement')}: <span className="font-mono">{q.data.last_settlement.id}</span></div>
              )}
              {q.data?.last_settlement?.settlement_date && (
                <div className="text-[11px] text-muted-foreground">{formatDate(q.data.last_settlement.settlement_date, locale)}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ ok }) {
  return ok ? (
    <CheckCircle2 className="h-5 w-5 text-[hsl(var(--status-success))]" />
  ) : (
    <XCircle className="h-5 w-5 text-[hsl(var(--status-danger))]" />
  );
}
